import React, { useEffect, useState } from 'react';
import {
  Container,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { URL, ResultType } from 'enum';
import AxiosUtils from 'utils/axiosUtils';
import ListUtils from 'utils/listUtils';
import LogUtils from 'utils/logUtils';
import StringUtils from 'utils/stringUtils';
import { ZipModel } from 'types/zipModel';
import { ZipResultModel } from 'types/zipResultModel';
import MessageDialogComponent from 'components/messageDialogComponent';
import LoadingDialogComponent from 'components/loadingDialogComponent';
import SearchListComponent from 'views/search/components/searchListComponent';

/***********************************************************************
 * 型定義
 ***********************************************************************/

/***********************************************************************
 * Style
 ***********************************************************************/
/**
 * スタイル定義
 * @name useStyles
 */
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: '10px',
    padding: 0,
    width: '90%',
  },
  card: {
    // margin: '20px',
  },
  cardContent: {},
  cardActions: {
    justifyContent: 'center',
  },
  text: {
    width: '90%',
  },
  back: {
    margin: '2%',
    textAlign: 'left',
  },
  button: {
    margin: 10,
    width: 200,
  },
  nativeSelect: {
    marginTop: theme.spacing(3),
    width: '90%',
  },
  selectLabel: {
    fontSize: 12,
    color: 'gray',
    width: '90%',
    marginTop: 2,
  },
}));

/***********************************************************************
 * メイン処理
 ***********************************************************************/
/**
 * 郵便番号検索画面
 * @name SearchView
 */
const SearchView: React.FC = () => {
  /**
   * クラス名
   * @name className
   * @member
   */
  const className: string = 'searchView';

  /**
   * スタイル
   * @name classes
   * @member
   */
  const classes = useStyles();

  /**
   * フック
   * @name [state, setState]
   * @member
   */
  const [state, setState] = useState({
    searchText: '',
    todofukenCode: '',
  });

  const [zipModelList, setZipModelList] = useState<Array<ZipModel>>(
    ListUtils.getList<ZipModel>()
  );

  // ------------------------------
  // MessageDialogパラメータ設定
  // ------------------------------
  /**
   * Openパラメータ
   */
  const [messageOpen, setMessageOpen] = useState(false);

  /**
   * messageパラメータ
   */
  const [message, setMessage] = useState('');

  // ------------------------------
  // LoadingDialogパラメータ設定
  // ------------------------------
  /**
   * Openパラメータ
   */
  const [loadingOpen, setLoadingOpen] = useState(false);

  /**
   * 副作用フック
   * @name useEffect
   * @function
   */
  useEffect(() => {
    /**
     * 初期設定処理
     * @name init
     * @event
     */
    const init = async () => {
      const methodName = 'init';
      try {
        LogUtils.info(`${className} ${methodName} start`);
        LogUtils.info(`${className} ${methodName} end`);
      } catch (e) {
        LogUtils.info(`${className} ${methodName} Error: ${e}`);
      }
    };
    init();
  }, []);
  /***********************************************************************
   * イベント処理
   ***********************************************************************/
  /**
   * 名称検索ボタンクリック
   * @name handleNameSearchClick
   * @event
   */
  const handleNameSearchClick = async () => {
    const methodName = 'handleNameSearchClick';
    try {
      LogUtils.info(`${className} ${methodName} start`);
      // 値のチェック
      if (isValid(state.searchText, false) === false) {
        return;
      }

      // 名称で検索
      await showDetail(URL.AddressSearch);

      LogUtils.info(`${className} ${methodName} end`);
    } catch (e) {
      showErrorDialog();
      LogUtils.info(`${className} ${methodName} Error: ${e}`);
    }
  };

  /**
   * 番号検索ボタンクリック
   * @name handleCodeSearchClick
   * @event
   */
  const handleCodeSearchClick = async () => {
    const methodName = 'handleCodeSearchClick';
    try {
      LogUtils.info(`${className} ${methodName} start`);

      // 値のチェック
      if (isValid(state.searchText, true) === false) {
        return;
      }

      // 番号で検索
      await showDetail(URL.ZipSearch);

      LogUtils.info(`${className} ${methodName} end`);
    } catch (e) {
      showErrorDialog();
      LogUtils.info(`${className} ${methodName} Error: ${e}`);
    }
  };

  /***********************************************************************
   * ローカルメソッド
   ***********************************************************************/
  /**
   * 正常値のチェック
   * @name isValid
   * @param target 対象文字列
   * @return {boolean} true: 正常、false: 異常
   */
  const isValid = (target: string, isNumber: boolean) => {
    const val: string = target.trim();

    if (!val) {
      showDialog('検索ワードを入力してください');
      return false;
    }
    if (isNumber) {
      let isNumberError: boolean = false;
      // 桁数チェック
      if (val.length > 7) {
        isNumberError = true;
      }
      // 数値チェック
      if (!StringUtils.isNumber(val)) {
        isNumberError = true;
      }
      if (isNumberError) {
        showDialog('7桁以下の数値で入力してください');
        return false;
      }
    } else {
      if (StringUtils.isHankakuKana(val) || StringUtils.isHankaku(val)) {
        showDialog('全角文字列で入力してください');
        return false;
      }
    }
    return true;
  };

  /**
   * 明細情報を表示する
   * @name showDetail
   * @param {URL} url URL
   * @return {void}
   */
  const showDetail = async (url: URL) => {
    // ローディング表示
    setLoadingOpen(true);

    // データ取得
    const zipResultModel = await AxiosUtils.accessPost<ZipResultModel>(
      url,
      state
    );

    try {
      // 一覧をクリア
      setZipModelList(ListUtils.getList<ZipModel>());
    } catch (e) {
      showDialog('通信エラーが発生しました');
      setLoadingOpen(false);
      return;
    }

    // apiでエラーが出た場合は、結果量が多い
    if (zipResultModel.resultType === ResultType.SystemError) {
      showDialog('通信エラーが発生しました');
      setLoadingOpen(false);
      return;
    }

    if (zipResultModel.zipModelList.length === 0) {
      showDialog('該当データがありません');
      setLoadingOpen(false);
      return;
    }
    // パラメータの設定
    setZipModelList(zipResultModel.zipModelList);

    // ローディング非表示
    setLoadingOpen(false);
  };

  /**
   * Dialogを表示する
   * @name showDialog
   * @param {string} メッセージ
   */
  const showDialog = (message: string) => {
    setMessage(message);
    setMessageOpen(true);
  };

  /**
   * ダイアログを表示する.
   * @name showErrorDialog
   */
  const showErrorDialog = () => {
    showDialog('システムエラーが発生しました');
  };

  /***********************************************************************
   * 画面の定義
   ***********************************************************************/
  return (
    <Container className={classes.root}>
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <TextField
            className={classes.text}
            required={true}
            id="searchText"
            label="住所か〒を入力"
            value={state.searchText}
            onChange={(e) => setState({ ...state, searchText: e.target.value })}
          />
        </CardContent>
        <CardActions className={classes.cardActions}>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={handleNameSearchClick}
          >
            住所で検索
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={handleCodeSearchClick}
          >
            〒で検索
          </Button>
        </CardActions>
      </Card>
      <SearchListComponent zipModelList={zipModelList}></SearchListComponent>
      <MessageDialogComponent
        message={message}
        open={messageOpen}
        setOpen={setMessageOpen}
      ></MessageDialogComponent>
      <LoadingDialogComponent open={loadingOpen}></LoadingDialogComponent>
    </Container>
  );
};

export default SearchView;
